import React from 'react';
import { FieldGroupSection, FieldGrid } from '../../common/FieldGroupSection';
import { AdditionalFieldsEditor } from '../../common/AdditionalFieldsEditor';
import EffectiveConfigPanel from '../EffectiveConfigPanel';
import { useI18n } from '../../../contexts/I18nContext';
import { customFieldsOf, hasOverride, keysOf, overriddenKeysOf, templateCustomFieldsOf } from '../../../services/deviceService';
import { CONFIG_FIELD_NAMES, usesFixedConfig } from '../../../config/configSchema';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { maskSecrets } from '../../../utils/secrets';
import { DeviceDetailCore } from '../useDeviceDetail';
import { useDevicesDetailsSettings } from './useDevicesDetailsSettings';
import './DevicesDetailsSettings.css';

interface Props {
  core: DeviceDetailCore;
}

/**
 * The device's effective configuration with the override editor. A P4GU or
 * a TMS2 reads the fixed key list (MQTT, syslog, general), shown grouped;
 * any other type only receives the additional configuration, so that
 * section is all it gets, with where it is inherited from. Passwords (the
 * MQTT connection's among them) are shown to ANEP staff and masked for a
 * client's members, in every block.
 */
export const DevicesDetailsSettings: React.FC<Props> = ({ core }) => {
  const { t } = useI18n();
  const { isAnepStaff } = useClientAccess();
  const { deviceDetail, template, hardwareTypes } = core;
  const s = useDevicesDetailsSettings(core);
  const overridden = hasOverride(deviceDetail);
  const fixedKeys = usesFixedConfig(deviceDetail?.deviceType);
  const shown = <T,>(value: T): T => (isAnepStaff ? value : maskSecrets(value));
  // Layer 1: the product's base configuration.
  const product = hardwareTypes.find((hw) => hw.code === deviceDetail?.deviceType);
  const productConfig = product?.config || {};
  const inheritedCustomFields = [...customFieldsOf(productConfig), ...templateCustomFieldsOf(template)];
  // Keys the gateway receives outside the fixed list and outside the custom fields.
  const customNames = new Set([
    ...inheritedCustomFields.map((c) => c.name),
    ...s.customFields.map((c) => c.name),
  ]);
  const extraKeys = Object.fromEntries(
    Object.entries(deviceDetail?.effective_config || {})
      .filter(([k]) => k !== 'custom_fields' && !CONFIG_FIELD_NAMES.includes(k) && !customNames.has(k)),
  );

  return (
    <div className="modal-content tab-content">
      <div className="settings-header">
        {!s.isEditingSettings ? (
          <button
            className="btn btn-secondary btn-sm"
            data-testid="btn-device-settings-edit"
            onClick={s.handleStartSettingsEdit}
            disabled={!deviceDetail}
          >
            Edit Settings
          </button>
        ) : (
          <div className="settings-edit-actions">
            <button className="btn btn-secondary btn-sm" data-testid="btn-device-settings-cancel" onClick={s.handleCancelSettingsEdit} disabled={s.savingSettings}>
              Cancel
            </button>
            <button className="btn btn-primary btn-sm" data-testid="btn-device-settings-save" onClick={s.handleSaveSettings} disabled={s.savingSettings}>
              {s.savingSettings ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="field-groups-container" data-testid="settings-groups" data-fixed-keys={fixedKeys ? 'yes' : 'no'} data-secrets={isAnepStaff ? 'shown' : 'masked'}>
        {fixedKeys && s.categorizedFields
          .filter((group) => !group.isSpecial && group.fields.length > 0)
          .map((group) => (
            <FieldGroupSection
              key={group.code}
              title={group.name}
              code={group.code}
              isExpanded={s.expandedGroups.includes(group.code)}
              onToggle={() => s.toggleGroupExpanded(group.code)}
              fieldCount={group.fields.length}
            >
              <FieldGrid
                fields={group.fields}
                isEditing={s.isEditingSettings}
                formValues={s.settingsForm}
                onChange={s.handleSettingsChange}
              />
            </FieldGroupSection>
          ))}

        <FieldGroupSection
          title={t('devices.additionalFieldsSection')}
          code="additional"
          isExpanded={s.expandedGroups.includes('additional')}
          onToggle={() => s.toggleGroupExpanded('additional')}
        >
          <AdditionalFieldsEditor
            customFields={s.customFields}
            jsonAdditionalConfig={Object.keys(extraKeys).length > 0 ? extraKeys : null}
            templateFields={inheritedCustomFields.map((c) => ({ name: c.name, value: String(c.value ?? '') }))}
            onChange={s.setCustomFields}
            isEditing={s.isEditingSettings}
            maskSecrets={!isAnepStaff}
          />
        </FieldGroupSection>

        {Object.keys(productConfig).length > 0 && (
          <FieldGroupSection
            title={t('devices.productConfiguration', { code: deviceDetail?.deviceType || '' })}
            code="product"
            isExpanded={s.expandedGroups.includes('product')}
            onToggle={() => s.toggleGroupExpanded('product')}
          >
            <div className="detail-grid">
              <div className="detail-item full-width">
                <span className="detail-label">{t('devices.productConfigurationHelp')}</span>
                <pre className="json-preview">{JSON.stringify(shown(productConfig), null, 2)}</pre>
              </div>
            </div>
          </FieldGroupSection>
        )}

        {deviceDetail?.template_id && (
          <FieldGroupSection
            title={t('devices.templateInformation')}
            code="template"
            isExpanded={s.expandedGroups.includes('template')}
            onToggle={() => s.toggleGroupExpanded('template')}
          >
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">{t('configSources.layers.template')}</span>
                <span className="detail-value">{template?.name || `ID: ${deviceDetail.template_id}`}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t('devices.configOverridden')}</span>
                <span className={`detail-value badge ${overridden ? 'badge-warning' : 'badge-success'}`}>
                  {overridden ? t('common.yes') : t('common.no')}
                </span>
              </div>
              {template?.json_config && (
                <div className="detail-item full-width">
                  <span className="detail-label">{t('devices.templateConfigJson')}</span>
                  <pre className="json-preview">{JSON.stringify(shown(template.json_config), null, 2)}</pre>
                </div>
              )}
              {overridden && (
                <div className="detail-item full-width">
                  <span className="detail-label">{t('devices.overrideJson')}</span>
                  <pre className="json-preview">{JSON.stringify(shown(deviceDetail.config_override), null, 2)}</pre>
                </div>
              )}
            </div>
          </FieldGroupSection>
        )}

        {deviceDetail && fixedKeys && (
          <EffectiveConfigPanel
            effectiveConfig={deviceDetail.effective_config}
            overriddenKeys={overriddenKeysOf(deviceDetail)}
            templateKeys={keysOf(template?.json_config as Record<string, unknown> | undefined)}
            templateName={template?.name}
            productName={product?.name}
            lastRequestAt={deviceDetail.last_request_at}
            lastRequestBy={deviceDetail.last_request_by}
            revealSecrets={isAnepStaff}
          />
        )}

      </div>
    </div>
  );
};
